import React, { useCallback, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { expenseAPI } from '../../services/api';
import useReport from '../reports/shared/useReport';
import KpiStrip from '../reports/shared/KpiStrip';
import ChartPanel from '../reports/shared/ChartPanel';
import ReportTable from '../reports/shared/ReportTable';
import ReportPrintLayout from '../reports/shared/ReportPrintLayout';
import PrintButton from '../reports/shared/PrintButton';
import DateRangeFilter, { getPresetRange } from '../reports/shared/DateRangeFilter';
import { PALETTE, axisProps, fmtMoney, fmtMoneyShort, fmtNum, tooltipStyle } from '../reports/shared/chartTheme';

// The Monthly Report tab of the Expenses module: spending as a month x department pivot, the way
// the old reporting tool presented it. Sub-categories roll up into their parent department so the
// columns stay readable; the backend sends flat rows and the pivot is built here because the
// department columns are data, not schema.

const ExpenseMonthlyReport = () => {
  const [range, setRange] = useState(() => getPresetRange('thisYear'));
  const [preset, setPreset] = useState('thisYear');

  const fetcher = useCallback(
    () => expenseAPI.getMonthlyReport(range.from, range.to),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  const { months, departments, columns, chartData } = useMemo(() => {
    const rows = data?.rows || [];
    const deptSet = new Set();
    const byMonth = {};
    rows.forEach((r) => {
      deptSet.add(r.department);
      const m = (byMonth[r.month] = byMonth[r.month] || { month: r.month, total: 0 });
      m[r.department] = (m[r.department] || 0) + Number(r.total);
      m.total += Number(r.total);
    });
    const departments = [...deptSet].sort((a, b) => a.localeCompare(b));
    const months = Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month));
    const columns = [
      { key: 'month', label: 'Month' },
      ...departments.map((d) => ({
        key: d,
        label: d,
        align: 'right',
        render: (r) => (r[d] ? fmtMoney(r[d]) : '—'),
        csv: (r) => r[d] || 0,
      })),
      {
        key: 'total', label: 'Total', align: 'right',
        render: (r) => <span className="font-semibold">{fmtMoney(r.total)}</span>,
        csv: (r) => r.total,
      },
    ];
    return { months, departments, columns, chartData: months };
  }, [data]);

  const totals = useMemo(() => {
    const grand = months.reduce((s, m) => s + m.total, 0);
    let biggestMonth = null;
    months.forEach((m) => { if (!biggestMonth || m.total > biggestMonth.total) biggestMonth = m; });
    const deptTotals = departments.map((d) => ({
      department: d,
      total: months.reduce((s, m) => s + (m[d] || 0), 0),
    }));
    deptTotals.sort((a, b) => b.total - a.total);
    return { grand, biggestMonth, topDept: deptTotals[0] };
  }, [months, departments]);

  const kpis = [
    { label: 'Total Spent', value: fmtMoney(totals.grand) },
    { label: 'Months', value: fmtNum(months.length) },
    { label: 'Avg / Month', value: months.length ? fmtMoney(Math.round(totals.grand / months.length)) : '—' },
    { label: 'Biggest Month', value: totals.biggestMonth?.month || '—', sub: totals.biggestMonth ? fmtMoney(totals.biggestMonth.total) : undefined },
    { label: 'Top Department', value: totals.topDept?.department || '—', sub: totals.topDept ? fmtMoney(totals.topDept.total) : undefined },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter
            value={range}
            preset={preset}
            onChange={(r, p) => { setRange(r); setPreset(p); }}
            presets={['thisMonth', 'lastMonth', 'last6Months', 'thisYear']}
          />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ChartPanel title="Monthly spending, stacked by department">
        {loading || chartData.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No expenses in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} tickFormatter={fmtMoneyShort} />
              <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
              <Legend />
              {departments.map((d, i) => (
                <Bar key={d} dataKey={d} stackId="1" fill={PALETTE[i % PALETTE.length]} radius={i === departments.length - 1 ? [4, 4, 0, 0] : undefined} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        title="Spending by month and department"
        columns={columns}
        rows={months}
        csvName={`expenses-monthly-${range.from}-to-${range.to}`}
        searchable={false}
        emptyText="No expenses in the selected range"
        footnote="Sub-categories are rolled up into their department."
      />

      <ReportPrintLayout
        title="Monthly Expense Report"
        subtitle={`Period: ${range.from} to ${range.to}`}
        kpis={kpis.map(({ label, value }) => ({ label, value }))}
        tables={[{ title: 'Spending by month and department', columns, rows: months }]}
      />
    </div>
  );
};

export default ExpenseMonthlyReport;
